import { moduleOrder } from "@/components/modules/registry";
import { DEFAULT_CHILD_AGE_GROUPS, type Event, type ModuleConfigMap } from "@/lib/event-schema";

// Slugs we can't allow because they collide with reserved platform routes.
// The platform serves /admin and /api at the top level, and the admin
// new-event form itself lives at /admin/events/new — without this guard,
// a user could create an event with slug "new" that would shadow the
// editor route for that event.
export const RESERVED_SLUGS: ReadonlySet<string> = new Set([
  "admin",
  "api",
  "assets",
  "donate",
  "icons",
  "new",
  "static",
]);

// Factories used by the admin "new event" form. Kept here (vs in the route)
// so the route file stays focused on the UI and the defaults are easy to
// tweak from one place.

const DEFAULT_POTLUCK_NOTICE =
  "All food must be halal. No pork, no alcohol, no shared utensils with non-halal cooking. When in doubt, ask.";

const POTLUCK_CATEGORIES = [
  { id: "main", label: "Main Dish", emoji: "🍚" },
  { id: "appetizer", label: "Appetizer/Side", emoji: "🥗" },
  { id: "dessert", label: "Dessert", emoji: "🍮" },
  { id: "drink", label: "Drink/Beverage", emoji: "🍵" },
  { id: "nuts", label: "Nuts and Sweets", emoji: "🍬" },
];

const COMPETITION_FIELDS: ModuleConfigMap["competition"]["fields"] = [
  {
    id: "participantName",
    label: "Your name",
    type: "text",
    required: true,
    publiclyVisible: true,
  },
  { id: "entryTitle", label: "Entry name", type: "text", required: true, publiclyVisible: true },
  { id: "description", label: "A line or two about it", type: "longtext", publiclyVisible: true },
  {
    id: "contactInfo",
    label: "Phone or email",
    type: "text",
    required: true,
    hint: "Kept private.",
    publiclyVisible: false,
  },
];

export function buildBlankEvent(input: {
  slug: string;
  title: string;
  tagline?: string;
  date?: string;
}): Event {
  const now = Date.now();
  const modules: Partial<ModuleConfigMap> = {
    hero: {
      enabled: true,
      title: `*${input.title}*`,
      subtitle: input.tagline ?? "",
      date: input.date || "Date to come",
      location: "Mobeen Center · Main Hall",
    },
    agenda: { enabled: false, title: "The day, hour by hour", items: [] },
    rsvp: {
      enabled: false,
      title: "RSVP for your family",
      description: "Tell us how many adults and kids are coming.",
      ctaLabel: "Confirm RSVP",
      childAgeGroups: [...DEFAULT_CHILD_AGE_GROUPS],
    },
    potluck: {
      enabled: false,
      title: "Bring a dish to share",
      description: "Every family contributes one dish.",
      notice: DEFAULT_POTLUCK_NOTICE,
      showSubmissions: true,
      categories: POTLUCK_CATEGORIES,
    },
    competition: {
      enabled: false,
      title: "Community competition",
      description: "Friendly contest, judged by the room.",
      entryNoun: "entry",
      participantNoun: "participant",
      judgingMode: "ranking",
      showEntries: false,
      fields: COMPETITION_FIELDS,
    },
    junior: {
      enabled: false,
      title: "While the grown-ups gather",
      description: "A parallel program for younger ones.",
      ageGroups: [],
    },
    donation: {
      enabled: false,
      title: "Help us host the next gathering",
      description: "Your contribution covers the hall and the food.",
      suggestedAmounts: [20, 50, 100, 250],
      allowCustom: true,
      stripeReady: false,
    },
  };
  return {
    slug: input.slug,
    title: input.title,
    tagline: input.tagline,
    status: "draft",
    modulesOrder: [...moduleOrder],
    modules,
    createdAt: now,
    updatedAt: now,
  };
}

export function cloneFrom(
  source: Event,
  overrides: { slug: string; title: string; tagline?: string; date?: string },
): Event {
  const cloned = structuredClone(source);
  // Treat empty/whitespace strings as "not provided" so a user who leaves
  // a field blank keeps the source's content instead of wiping it.
  const taglineOverride = overrides.tagline?.trim() ? overrides.tagline : undefined;
  const dateOverride = overrides.date?.trim() ? overrides.date : undefined;

  cloned.slug = overrides.slug;
  cloned.title = overrides.title;
  cloned.tagline = taglineOverride ?? source.tagline;
  cloned.status = "draft";
  cloned.createdAt = Date.now();
  cloned.updatedAt = Date.now();
  if (cloned.modules.hero) {
    cloned.modules.hero = {
      ...cloned.modules.hero,
      title: `*${overrides.title}*`,
      subtitle: taglineOverride ?? cloned.modules.hero.subtitle,
      date: dateOverride ?? "Date to come",
      // The eyebrow is event-specific copy; clear it so the new event
      // sets its own via the admin editor.
      eyebrow: undefined,
      // Rewrite any internal-link CTAs that pointed at the source event's
      // slug so they target the new event instead. External / root links
      // are left untouched.
      ctas: cloned.modules.hero.ctas?.map((cta) => ({
        ...cta,
        to: rewriteInternalPath(cta.to, source.slug, overrides.slug),
      })),
    };
  }
  // Force a fresh banner + theme so the new event picks up its own.
  if (cloned.modules.hero?.banner) cloned.modules.hero.banner = undefined;
  cloned.theme = undefined;

  // Clear event-specific schedule. The agenda items in the source are
  // tied to that event's day (specific times, named speakers, etc.) and
  // do not survive a copy meaningfully; the date label likewise.
  if (cloned.modules.agenda) {
    cloned.modules.agenda = {
      ...cloned.modules.agenda,
      items: [],
      dateLabel: dateOverride ?? undefined,
    };
  }

  // Donation totals are running state, not config. Suggested amounts and
  // goal stay; raised resets so the progress bar starts from zero.
  if (cloned.modules.donation) {
    cloned.modules.donation = {
      ...cloned.modules.donation,
      raisedCents: undefined,
    };
  }

  return cloned;
}

function rewriteInternalPath(to: string, fromSlug: string, toSlug: string): string {
  if (to === `/${fromSlug}`) return `/${toSlug}`;
  const prefix = `/${fromSlug}/`;
  if (to.startsWith(prefix)) return `/${toSlug}/${to.slice(prefix.length)}`;
  return to;
}
